import { motion } from "framer-motion";
import { Zap } from "lucide-react";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

// Sessão UI-1 — DailyMissionCard
//
// Circle progress radial gauge (~180px) mostrando done/total dos hábitos
// do dia. Stroke-dasharray animado pra fill. Microcopy sob o círculo
// muda com 0/done/all-done.

const SIZE = 180;
const STROKE = 14;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function microcopy(done, total) {
  if (total === 0) return "Configure seus hábitos pra começar.";
  if (done === 0) return "Comece sua sequência.";
  if (done === total) return "Missão cumprida hoje. Excelente.";
  const remaining = total - done;
  return `${remaining} restante${remaining === 1 ? "" : "s"} — não quebre a corrente.`;
}

export default function DailyMissionCard({ done = 0, total = 0 }) {
  const safeTotal = Math.max(0, total);
  const safeDone = Math.max(0, Math.min(done, safeTotal));
  const pct = safeTotal > 0 ? safeDone / safeTotal : 0;
  const strokeOffset = CIRCUMFERENCE * (1 - pct);

  return (
    <Card
      variant="elevated"
      className="glass-card glow-hover relative overflow-hidden p-6 md:p-7 min-h-[260px] flex flex-col"
      data-testid="daily-mission-card"
    >
      {/* Sutil tonalização amber */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background: "radial-gradient(circle at 90% 0%, rgba(255,200,87,0.06), transparent 40%)",
        }}
      />

      <div className="relative flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-status-warning/10 flex items-center justify-center">
            <Zap className="w-4 h-4 text-status-warning" aria-hidden="true" />
          </div>
          <span
            className="text-caption font-semibold uppercase text-status-warning/80"
            style={{ letterSpacing: "0.12em" }}
          >
            Missão do dia
          </span>
        </div>
      </div>

      <div className="relative flex-1 flex flex-col items-center justify-center py-2">
        <div className="relative" style={{ width: SIZE, height: SIZE }}>
          <svg
            width={SIZE}
            height={SIZE}
            viewBox={`0 0 ${SIZE} ${SIZE}`}
            aria-hidden="true"
          >
            {/* Track */}
            <circle
              cx={SIZE / 2} cy={SIZE / 2} r={RADIUS}
              fill="none"
              stroke="rgba(255,200,87,0.15)"
              strokeWidth={STROKE}
            />
            {/* Progress (animated via stroke-dashoffset) */}
            <motion.circle
              cx={SIZE / 2} cy={SIZE / 2} r={RADIUS}
              fill="none"
              stroke="url(#missionGradient)"
              strokeWidth={STROKE}
              strokeLinecap="round"
              strokeDasharray={CIRCUMFERENCE}
              initial={{ strokeDashoffset: CIRCUMFERENCE }}
              animate={{ strokeDashoffset: strokeOffset }}
              transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
              transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
            />
            <defs>
              <linearGradient id="missionGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FFC857" />
                <stop offset="100%" stopColor="#00FFA3" />
              </linearGradient>
            </defs>
          </svg>
          {/* Centro: done/total */}
          <div
            className="absolute inset-0 flex flex-col items-center justify-center"
            data-testid="mission-progress"
          >
            <span
              className={cn(
                "font-display font-bold text-foreground tabular-nums leading-none",
                "text-[40px] md:text-[44px]",
              )}
              aria-label={`${safeDone} de ${safeTotal} hábitos concluídos`}
            >
              {safeDone}
              <span className="text-foreground-subtle font-semibold">/{safeTotal}</span>
            </span>
            <span className="mt-1 text-caption text-foreground-muted">
              hábitos
            </span>
          </div>
        </div>

        <p className="mt-4 text-caption text-foreground-muted text-center max-w-xs">
          {microcopy(safeDone, safeTotal)}
        </p>
      </div>
    </Card>
  );
}
